import * as result from "lib/result"
import {
	AccountInfo,
	InteractionStatus,
	IPublicClientApplication,
	SilentRequest,
} from "@azure/msal-browser"
import { useMsal } from "@azure/msal-react"
import {
	useEffect,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
	createContext,
	PropsWithChildren,
} from "react"
import { useSelector, useDispatch } from "react-redux"
import { SetupAxiosInterceptors, TokenSlice, UserSlice, Store } from "store"
import * as util from "lib/util"
import * as hooks from "lib/hooks"

function useAuthContext() {
	const { instance, inProgress } = useMsal()
	const token = useSelector(TokenSlice.selectors.selectToken)
	const permissionErr = useSelector(TokenSlice.selectors.selectPermissionError)
	const permsLoading = useSelector(UserSlice.selectors.selectLoading)
	const dispatch = useDispatch()

	const _fetchToken = hooks.UseAction(TokenSlice.actions.fetchToken)
	const [shouldLogout, setShouldLogout] = useState(false)
	const hasFetchedToken = useRef(false)

	const account = useMemo<Opt<AccountInfo>>(() => util.GetMsalAccount(instance), [instance])

	const logout = useCallback(() => setShouldLogout(true), [])

	const acquireTokenSilently = useCallback(
		async ({ interactive } = { interactive: false }): Promise<Result<string, Error>> => {
			const req: SilentRequest = {
				scopes: ["https://graph.microsoft.us/.default"],
				account: account.Expect("No account found"),
				forceRefresh: true,
			}

			const method = interactive
				? instance.acquireTokenPopup.bind(instance)
				: instance.acquireTokenSilent.bind(instance)

			try {
				const response = await method(req)
				if (response && !response.idToken.IsEmpty()) {
					return result.Ok(response.idToken)
				}
				return result.Err(new Error("No access token acquired"))
			} catch (error: any) {
				return result.Err(error)
			}
		},
		[instance, account]
	)

	const [ejectInterceptor, setEjectInterceptor] = hooks.UseOption<() => void>()
	const tokenWatcher = hooks.UseValueWatcher<int>()
	const tokenRetryCt = useRef(0)
	const maxTokenRetries = 15
	const mccApi = hooks.UseMccApi()

	const handleTokenRefresh = useCallback(() => {
		log.Info("Token refresh requested from axios interceptor")
		tokenWatcher.clear()
		_fetchToken()
	}, [_fetchToken])

	const handleTokenFetchAsync = useCallback(async () => {
		if (!instance) {
			logout()
			return
		}
		if (tokenRetryCt.current >= maxTokenRetries) {
			log.Infof("Max token retries reached: %d. Signing out.", maxTokenRetries)
			logout()
			return
		}

		tokenRetryCt.current++
		tokenWatcher.clear()

		const tokenResult = await acquireTokenSilently()
		if (tokenResult.IsErr()) {
			log.Warn("Token acquisition error")
			logout()
			return
		}

		const token = tokenResult.Expect("Token shouldn't be empty")
		if (token.IsEmpty()) {
			logout()
			return
		}

		ejectInterceptor.IfSome((eject) => eject())
		const eject = SetupAxiosInterceptors(
			TokenSlice.selectors.selectAxios(Store.getState()),
			token,
			handleTokenRefresh
		)
		setEjectInterceptor(() => eject)

		const { timeUntilExp } = util.GetTokenExp(token)
		dispatch(TokenSlice.actions.setToken(token))

		try {
			const res = await mccApi.Permissions()

			// Check if user has no menu items (no permissions)
			if (!res.user.menuList || res.user.menuList.length === 0) {
				log.Warn("User has no menu items - no permissions granted")
				dispatch(TokenSlice.actions.setPermissionError("No permissions granted"))
				return
			}

			dispatch(
				UserSlice.actions.setUserPermissions({
					menuList: res.user.menuList,
					permissionList: res.permissionList,
					user: res.user,
					userRoles: res.getUserRoles,
				})
			)

			const delay = Math.max(timeUntilExp - 60000, 1000) // minimum 1s delay
			tokenWatcher.watch(
				delay,
				() => {
					log.Info("Token expired, fetching new token")
					handleTokenFetchAsync()
				},
				delay
			)
		} catch (err) {
			log.Errorf("Permission check failed: %j", err)
			dispatch(TokenSlice.actions.setPermissionError("Unauthorized"))
		}
	}, [
		acquireTokenSilently,
		dispatch,
		ejectInterceptor,
		handleTokenRefresh,
		instance,
		logout,
		mccApi,
		setEjectInterceptor,
		tokenWatcher,
	])

	const fetchToken = useCallback(() => {
		if (inProgress === InteractionStatus.None) {
			_fetchToken()
		}
	}, [_fetchToken, inProgress])

	useEffect(() => {
		if (shouldLogout && inProgress === InteractionStatus.None) {
			setShouldLogout(false)
			instance.logoutRedirect({
				postLogoutRedirectUri: window.location.origin,
			})
		}
	}, [shouldLogout, inProgress, instance])

	const [loading, setLoading] = useState(true)

	useEffect(() => {
		if (account.IsSome() && !hasFetchedToken.current) {
			setLoading(false)
			if (!shouldLogout) {
				handleTokenFetchAsync()
				hasFetchedToken.current = true
			}
		}
	}, [account, shouldLogout, handleTokenFetchAsync])

	return {
		instance,
		inProgress,
		account,
		token,
		fetchToken,
		permissionErr,
		loading: loading || permsLoading,
	}
}

export interface UseAuthReturn {
	instance: IPublicClientApplication
	inProgress: InteractionStatus
	account: Opt<AccountInfo>
	token: string
	fetchToken: () => void
	permissionErr: string
	loading: bool
}

export function UseAuth(): UseAuthReturn {
	const context = useContext(authCtx)
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider")
	}
	return context
}

const authCtx = createContext<UseAuthReturn | null>(null)

export function AuthProvider({ children }: PropsWithChildren) {
	const auth = useAuthContext()
	return <authCtx.Provider value={auth}>{children}</authCtx.Provider>
}
